import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { PopoverPosition } from '@blueprintjs/core';
import { omit, isEmpty } from 'lodash';
import { MAX_TRACE_ITEMS } from '@kentik/ui-shared/synthetics/constants';
import { Box, Card, EmptyState, Flex, Text, Icon, Button, Popover, Tag, Tooltip } from 'core/components';
import { Field, Form, GroupedMultiSelect, InputGroup, Select, Switch } from 'core/form';
import MultiSlider, { Handle } from 'core/components/MultiSlider';
import { safelyParseJSON } from 'core/util';
import { getTraceroutes } from 'app/stores/synthetics/utils';
import AgentSelectorCompact from 'app/views/synthetics/components/agentSelector/AgentSelectorCompact';
import { BaseAgentRenderer } from 'app/views/synthetics/components/AgentRenderer';
import { FiInfo } from 'react-icons/fi';
import Traceroute from './Traceroute';
import TestPathHops from './TestPathHops';

const fields = {
  selectedAgents: {
    label: 'Show',
    defaultValue: []
  },
  selectedIps: {
    defaultValue: []
  },
  groupBy: {
    label: 'Group hops by',
    options: [
      { label: 'No Grouping', value: 'none' },
      { label: 'ASN', value: 'asn' },
      { label: 'Site', value: 'site' }
    ],
    defaultValue: 'none'
  },
  percSuccessThreshold: {
    defaultValue: 0.9,
    rules: 'numeric',
    messages: { numeric: 'Not a valid number' },
    transform: {
      in: (value) => {
        if (value === '') {
          return 0;
        }
        return value * 100;
      },
      out: (value) => {
        if (value === '') {
          return 0;
        }
        return value / 100;
      }
    }
  },
  deltaLatencyThreshold: {
    defaultValue: 50,
    rules: 'numeric',
    messages: { numeric: 'Not a valid number' }
  },
  moreThanExpected: {
    label: 'Highlight links exceeding geo latency estimate',
    defaultValue: true
  },
  collapseFromStart: {
    label: 'After Agents',
    defaultValue: 0
  },
  collapseFromEnd: {
    label: 'Before Target',
    defaultValue: 0
  },
  openPaths: {
    defaultValue: null
  },
  collapseTimeouts: {
    label: 'Collapse Timeouts',
    defaultValue: true
  }
};

@inject('$app', '$exports', '$auth', '$dataviews', '$syn')
@Form({ fields, options: { name: 'Traceroute Options' } })
@observer
class TestPath extends Component {
  static defaultProps = {
    allowExport: true,
    isPreview: false
  };

  state = {
    isOpen: false
  };

  constructor(props) {
    super(props);

    const { $exports, form, allowExport, testResults } = this.props;

    if (allowExport) {
      $exports.getSettings().then(({ hashedTracerouteOpts }) => {
        let savedTracerouteOpts = {};

        if (hashedTracerouteOpts) {
          savedTracerouteOpts = hashedTracerouteOpts || {};
        } else if (window.localStorage) {
          savedTracerouteOpts = safelyParseJSON(localStorage.getItem('tracerouteOpts')) || {};
        }

        form.setValues({ ...savedTracerouteOpts });
      });
    }

    // initialize selected agent ids and target ips
    // the target ips are a list of objects that provide an ip and optional agent id when available
    form.setValues({
      selectedAgents: testResults.lastFetchedTraceParams.agentIds,
      selectedIps: testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip) || []
    });

    form.onChange = this.handleFormChange;
  }

  componentDidUpdate(prevProps) {
    const { form, traceroutes, testResults } = this.props;

    if (traceroutes !== prevProps.traceroutes) {
      form.setValues({
        selectedAgents: testResults.lastFetchedTraceParams.agentIds
      });
    }
  }

  handleFormChange = ({ field }) => {
    const { form, testResults } = this.props;

    if (field.name === 'selectedAgents' || field.name === 'selectedIps') {
      // when we update selected agents or ips, we will only fetch for new trace results when there are new values

      // start by getting the currently selected agents and ips
      // and initialize comparison against the agents we last fetched for
      const { selectedAgents, selectedIps } = form.getValues();
      let compareTo = testResults.lastFetchedTraceParams.agentIds;

      if (field.name === 'selectedIps') {
        // if we're comparing against ips, get a list of the ips from the last fetch
        compareTo = testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip);
      }

      // determine whether any values have changed
      const hasChangedData = field.value.filter((item) => compareTo.indexOf(item) === -1).length > 0;

      if (hasChangedData && selectedAgents.length && (selectedIps.length || !this.targetOptions.length)) {
        // if selections have changed in either agents or targets AND we have something to fetch for
        // the logic that checks for blank target options covers a scenario where a test that relies on targets returned from the trace query that contains only offline agents can still re-fetch
        // run the trace results query again using the current date range and the currently selected agents and ips
        testResults.fetchTraceResults({
          start_time: testResults.actualStartDate,
          end_time: testResults.actualEndDate,
          agent_ids: selectedAgents,
          target_ips: selectedIps
        });
      }
    }

    this.saveFormChange();
  };

  saveFormChange = () => {
    const { $exports, form, allowExport } = this.props;
    const values = form.getValues();

    if (allowExport) {
      if (window.localStorage) {
        localStorage.setItem('tracerouteOpts', JSON.stringify(omit(values, ['selectedAgents', 'selectedIps'])));
      }
      $exports.setHash({ hashedTracerouteOpts: omit(values, ['selectedAgents', 'selectedIps']) });
    }
  };

  onGroupByChange = ({ value: groupBy }) => {
    const { form } = this.props;

    form.setValues({
      groupBy,
      collapseFromStart: 0,
      collapseFromEnd: 0
    });

    this.saveFormChange();
  };

  onOpenPaths = (agentID, target_ip) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? [...openPaths, { agentID, target_ip }] : [{ agentID, target_ip }]
    });

    this.saveFormChange();
  };

  closeOpenPaths = (agentID) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? openPaths.filter((path) => path.agentID !== agentID) : null
    });

    this.saveFormChange();
  };

  setPaths = (agentIDs) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? openPaths.filter((path) => agentIDs.find((agentID) => path.agentID === agentID)) : null
    });

    this.saveFormChange();
  };

  // returns a list of select options that hydrate the agent selector
  get agentOptions() {
    const { $syn, agents } = this.props;
    let options = [];

    if (agents && agents.length > 0) {
      options = $syn.agents
        .getOptionsForAgentsByIds(agents)
        .map((agent) => ({
          ...agent,
          value: `${agent.value}`
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return options;
  }

  /*
    Returns a list of select options that hydrate the target selector
    Select options support the following:
      - option values will always be an ip, but will support shipping agent options where available so we can render agents in the combo
  */
  get targetOptions() {
    const { $syn, testResults } = this.props;
    // allTargets contains the comprehensive list of ips returned from the last trace results fetch
    const { allTargets = [] } = testResults.lastFetchedTraceParams || {};

    return allTargets
      .map(({ target_ip, target_agent_id }) => {
        const agentOptions = $syn.agents.getOptionsForAgentsByIds([target_agent_id])[0]; // try to get agent options

        return {
          label: target_ip,
          value: target_ip,
          filterLabel: `${agentOptions?.label || ''}-${target_ip}`, // support searching by ip and agent label where available
          agentOptions
        };
      })
      .sort((a, b) => a.filterLabel.localeCompare(b.filterLabel));
  }

  /*
    Renderer for target options.
    All options are rendered as checkboxes with a target ip or an agent renderer
  */
  targetOptionFormatter = (opts) => {
    const { value, agentOptions } = opts;
    return (
      <Box ml={1} key={value} title={value}>
        {agentOptions ? (
          <BaseAgentRenderer {...agentOptions} locationInfoProps={{ ellipsis: true }} showLocationInfo small />
        ) : (
          value
        )}
      </Box>
    );
  };

  resetForm = () => {
    const { form, testResults } = this.props;

    form.reset();
    form.setValues({
      selectedAgents: testResults.lastFetchedTraceParams.agentIds,
      selectedIps: testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip) || []
    });

    this.saveFormChange();
  };

  render() {
    const { testResults, traceroutes, onHover, form, onNodeSelect, selectedNode, isPreview } = this.props;

    const { traceResults, traceResultTimeMs, traceTimelineBounds } = testResults;
    const { xMin, xMax, xAxisMin, xAxisMax, delta } = traceTimelineBounds;
    const { max_asn_path_count, max_site_path_count, is_result_truncated } = traceResults;

    const { isOpen } = this.state;

    const {
      deltaLatencyThreshold,
      collapseFromStart,
      collapseFromEnd,
      groupBy,
      openPaths,
      percSuccessThreshold,
      selectedAgents,
      selectedIps,
      collapseTimeouts,
      moreThanExpected
    } = form.getValues();

    const { label: groupByLabel } = fields.groupBy.options.find((o) => o.value === groupBy);
    const groupBtnText =
      groupBy === 'none'
        ? 'None or ASN...' // 'ASN, Site...'
        : `${groupByLabel}: ${collapseFromStart} after agent, ${collapseFromEnd} before target`;
    const maxPathCount = (groupBy === 'asn' && max_asn_path_count) || (groupBy === 'site' && max_site_path_count) || 0;
    const hasResults = !isEmpty(traceResults);
    let time = isPreview ? 0 : traceResultTimeMs;

    if (isPreview && traceroutes?.length > 0) {
      // always take the last timestamp, we'll rollup results there for preview
      time = traceroutes[traceroutes.length - 1]?.time || 0;
    }

    return (
      <Box>
        <Card position="sticky" top="-16px" zIndex={2} display="flex" justifyContent="space-between" p={1} mb={2}>
          <Text fontWeight="bold">Filters</Text>
          <Flex flexDirection="column" alignItems="flex-end" gap={2}>
            <Flex>
              <Flex alignItems="center" mr={2} gap={1}>
                <Text small heavy>
                  Show
                </Text>
                <Tooltip
                  content={
                    <Box width={200}>
                      Select up to {MAX_TRACE_ITEMS} agents and targets.
                      <br />
                      Type <Text fontStyle="italic">selected</Text> in the selector filter to show only the currently
                      selected items.
                    </Box>
                  }
                >
                  <Icon icon="info-sign" color="primary" style={{ display: 'block' }} />
                </Tooltip>
                <Box width={230}>
                  <AgentSelectorCompact
                    mb={0}
                    agentOptions={this.agentOptions}
                    selectedAgents={selectedAgents}
                    showLabel={false}
                    disabled={isPreview || this.agentOptions.length === 1}
                    maxItems={MAX_TRACE_ITEMS}
                  />
                </Box>

                <Box width={230}>
                  {this.targetOptions.length > 0 && (
                    <Field
                      name="selectedIps"
                      options={this.targetOptions}
                      disabled={isPreview || this.targetOptions.length === 1}
                      mb={0}
                    >
                      <GroupedMultiSelect
                        unselectedGroupName="target"
                        optionFormatter={this.targetOptionFormatter}
                        maxItems={MAX_TRACE_ITEMS}
                      />
                    </Field>
                  )}
                </Box>
              </Flex>
              <Box mr={2}>
                <Popover
                  minimal={false}
                  position={PopoverPosition.BOTTOM}
                  content={
                    <Box p={2}>
                      <Field name="groupBy" onChange={this.onGroupByChange} small>
                        <Select />
                      </Field>
                      {maxPathCount > 1 && (
                        <Box mx={1} mb={1}>
                          <MultiSlider min={0} max={maxPathCount} disabled={maxPathCount <= 1}>
                            <Handle
                              intentBefore="primary"
                              intentAfter="none"
                              value={collapseFromStart}
                              onChange={(val) => form.getField('collapseFromStart').onChange(val)}
                            />
                            <Handle
                              intentAfter="primary"
                              value={maxPathCount - collapseFromEnd}
                              onChange={(val) => form.getField('collapseFromEnd').onChange(maxPathCount - val)}
                            />
                          </MultiSlider>
                        </Box>
                      )}
                    </Box>
                  }
                >
                  <Flex alignItems="center">
                    <Text as="div" small mr="4px">
                      Group hops by
                    </Text>
                    <Button rightIcon="caret-down" active={isOpen} small>
                      {groupBtnText}
                    </Button>
                  </Flex>
                </Popover>
              </Box>
              <Flex alignItems="center">
                <Text as="div" small mr="4px">
                  Highlight
                </Text>
                <Box mr={1}>
                  <Popover
                    minimal={false}
                    position={PopoverPosition.BOTTOM}
                    content={
                      <Box p={1}>
                        <Flex alignItems="center" m={1}>
                          <Text as="div" whiteSpace="nowrap" mr={1} small>
                            When latency is at least
                          </Text>
                          <Field name="deltaLatencyThreshold" mb={0}>
                            <InputGroup type="number" min="0" rightElement={<Tag>ms</Tag>} width={100} />
                          </Field>
                        </Flex>
                      </Box>
                    }
                  >
                    <Button rightIcon="caret-down" small>
                      Latency &gt;= {deltaLatencyThreshold}ms
                    </Button>
                  </Popover>
                </Box>
                <Box>
                  <Popover
                    minimal={false}
                    position={PopoverPosition.BOTTOM}
                    content={
                      <Box p={1}>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Box whiteSpace="nowrap" mr={1}>
                            When success rate is at or below
                          </Box>
                          <Field name="percSuccessThreshold" mb={0}>
                            <InputGroup
                              type="number"
                              min="0"
                              max="100"
                              rightElement={<Tag>%</Tag>}
                              width={120}
                            />
                          </Field>
                        </Flex>
                      </Box>
                    }
                  >
                    <Button rightIcon="caret-down" small>
                      {/* round the output to prevent precision goofiness such as 0.55 * 100 = 55.00000000000001  */}
                      Success Rate &lt;= {Math.round(percSuccessThreshold * 100)}%
                    </Button>
                  </Popover>
                </Box>
              </Flex>
            </Flex>
            <Flex alignItems="center">
              <Field name="collapseTimeouts" showLabel={false} small mb={0} mr={2}>
                <Switch showFieldLabel />
              </Field>
              <Flex alignItems="center">
                <Field name="moreThanExpected" showLabel={false} small mb={0}>
                  <Switch showFieldLabel />
                </Field>
                <Tooltip content="Turn this on if you want us to compare latency measured with latency calculated using geo-ip distance">
                  <Icon iconSize={14} icon={FiInfo} color="primary" ml="4px" />
                </Tooltip>
              </Flex>
              <Button onClick={this.resetForm} ml={2} small>
                Reset
              </Button>
            </Flex>
          </Flex>
        </Card>
        {!hasResults && (
          <Card p={1} py={3}>
            <EmptyState icon="disable" title="No results" />
          </Card>
        )}
        {hasResults && !isPreview && (
          <TestPathHops
            isTraceResultsTruncated={is_result_truncated}
            traceroutes={getTraceroutes({ selectedAgentIds: selectedAgents, selectedIps, traceResults })} // filtered traceroutes by selected agents, the Traceroute component handles its own logic for that
            resultTimeMs={traceResultTimeMs}
            onHover={onHover}
            xMin={xMin}
            xMax={xMax}
            xAxisMin={xAxisMin}
            xAxisMax={xAxisMax}
            delta={delta}
            mb={5}
          />
        )}
        {hasResults && !!selectedAgents.length && (
          <Traceroute
            time={time}
            traceroutes={traceroutes}
            selectedAgents={selectedAgents}
            selectedIps={selectedIps}
            groupBy={groupBy}
            collapseFromStart={collapseFromStart}
            collapseFromEnd={collapseFromEnd}
            openPaths={openPaths}
            onOpenPaths={this.onOpenPaths}
            closeOpenPaths={this.closeOpenPaths}
            setPaths={this.setPaths}
            deltaLatencyThreshold={deltaLatencyThreshold}
            percSuccessThreshold={percSuccessThreshold}
            onNodeSelect={onNodeSelect}
            selectedNode={selectedNode}
            collapseTimeouts={collapseTimeouts}
            moreThanExpected={moreThanExpected}
          />
        )}
      </Box>
    );
  }
}

export default TestPath;
